import React, { createContext, useState, ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Content } from '@tiptap/react';
import { getFaqsContent, putFaqsContent } from '../services/sourceService';

interface LastEditedBy {
  email: string;
  group: string;
  firstName: string;
  role: string;
  lastName: string;
}

interface HelpContentContextType {
  content: Content;
  initalContent: Content;
  lastEditedBy: LastEditedBy | null;
  lastEditedAt: number | null;
  isLoading: boolean;
  error: string | null;
  fetchContent: () => Promise<void>;
  saveContent: (content: Content) => Promise<void>;
  updateContent: (content: Content) => void;
}

export const HelpContentContext = createContext<HelpContentContextType>({
  content: null,
  initalContent: null,
  lastEditedBy: null,
  lastEditedAt: null,
  isLoading: false,
  error: null,
  fetchContent: async () => {},
  saveContent: async () => {},
  updateContent: () => {},
});

interface HelpContentProviderProps {
  children: ReactNode;
}

export const HelpContentProvider: React.FC<HelpContentProviderProps> = ({ children }) => {
  const [content, setContent] = useState<Content | null>(null);
  const [initalContent, setInitialContent] = useState<Content | null>(null);
  const [lastEditedBy, setLastEditedBy] = useState<LastEditedBy | null>(null);
  const [lastEditedAt, setLastEditedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery(['faqsContent'], getFaqsContent, {
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    onError: () => {
      setError('Failed to load help content');
    },
  });

  useEffect(() => {
    if (data && data?.content?.content) {
      setContent(data.content.content);
      setInitialContent(data.content.content);
      setLastEditedBy(data.lastEditedBy || null);
      setLastEditedAt(data.lastEditedAt || null);
      setError(null);
    } else if (data) {
      setError('Invalid response format from API');
    }
  }, [data]);

  const fetchContent = async () => {
    await refetch();
  };

  // Save content to the API
  const saveContent = async (updatedContent: Content) => {
    try {
      const response = await putFaqsContent({ content: updatedContent });

      if (response && response?.content?.content) {
        setContent(response?.content?.content);
        setInitialContent(response?.content?.content);
        setLastEditedBy(response.lastEditedBy || null);
        setLastEditedAt(response.lastEditedAt || null);
        setError(null);
      } else {
        console.error({ message: 'Invalid response format after save:', response });
      }

      return Promise.resolve();
    } catch (err) {
      console.error({ message: 'Error saving help content:', err });
      setError('Failed to save help content');
      return Promise.reject(err);
    }
  };

  const updateContent = (updatedContent: Content) => {
    setContent(updatedContent);
  };

  return (
    <HelpContentContext.Provider
      value={{
        content,
        initalContent,
        lastEditedBy,
        lastEditedAt,
        isLoading: isLoading || isFetching,
        error,
        fetchContent,
        saveContent,
        updateContent,
      }}
    >
      {children}
    </HelpContentContext.Provider>
  );
};

export const useHelpContent = () => React.useContext(HelpContentContext);
